import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Non-sensitive UI flag only — NOT a credential. The real access/refresh tokens
// live in httpOnly cookies set by the backend (somo-circle1-api Issue #302) and
// are never readable from JS. This flag just lets the SPA decide whether to
// attempt an authenticated call before the first request round trip comes back;
// the backend is the sole source of truth for whether a session is actually valid.
const SESSION_FLAG = 'isLoggedIn';

class AuthManager {
    constructor() {
        this.lastActivity = Date.now();

        // How often to proactively refresh the httpOnly access-token cookie
        // (access token itself expires in 15 min - see backend generateToken)
        this.REFRESH_INTERVAL = 13 * 60 * 1000;
        this.INACTIVITY_TIMEOUT = 30 * 60 * 1000;  // Logout on inactivity (30 min)

        // Track timer IDs to prevent memory leaks
        this.inactivityTimer = null;
        this.refreshTimer = null;
        this.isInitialized = false;

        // Cache for validateUserRole to avoid redundant API calls across components
        this.roleCache = { result: null, timestamp: null, CACHE_DURATION: 30 * 1000 };

        // Shared in-flight refresh so concurrent 401s (e.g. several widgets fetching
        // at once) don't each call /refresh-token - if the backend rotates refresh
        // tokens, a second concurrent refresh would fail and force a bogus logout.
        this.refreshPromise = null;
    }

    init() {
        // Prevent multiple initialization to avoid timer leaks
        if (this.isInitialized) {
            return;
        }

        this.trackActivity();
        this.setupInactivityTimer();
        this.setupRefreshTimer();
        this.isInitialized = true;
    }

    // Called after a successful login/MFA verify, once the backend has set the
    // httpOnly auth cookies, to flip the local "am I logged in" UI flag.
    markLoggedIn() {
        localStorage.setItem(SESSION_FLAG, 'true');
    }

    isAuthenticated() {
        return localStorage.getItem(SESSION_FLAG) === 'true';
    }

    // `silent` suppresses the "Session expired" toast - used for passive session
    // probes (initial route guard, background role checks) where a stale local
    // isLoggedIn flag failing validation isn't a real, user-visible expiry event.
    // If a refresh is already in flight, every caller shares that one promise.
    async refreshAccessToken({ silent = false } = {}) {
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        this.refreshPromise = (async () => {
            try {
                // No body needed - the refresh token travels as an httpOnly cookie.
                await axios.post(`${BASE_URL}/users/refresh-token`, {}, { withCredentials: true });
                this.markLoggedIn();
                window.dispatchEvent(new CustomEvent('auth:token-refreshed'));
            } catch (err) {
                this.logout();
                if (!silent) {
                    toast.error('Session expired. Please log in again.');
                }
                throw err;
            } finally {
                this.refreshPromise = null;
            }
        })();

        return this.refreshPromise;
    }


    trackActivity() {
        const resetActivity = () => {
            this.lastActivity = Date.now();
        };

        ['click', 'mousemove', 'keydown', 'scroll', 'touchstart'].forEach(evt =>
            window.addEventListener(evt, resetActivity)
        );
    }

    setupInactivityTimer() {
        // Clear existing timer to prevent leaks
        if (this.inactivityTimer) {
            clearInterval(this.inactivityTimer);
        }

        this.inactivityTimer = setInterval(() => {
            if (Date.now() - this.lastActivity > this.INACTIVITY_TIMEOUT) {
                this.logout();
                window.dispatchEvent(new CustomEvent('auth:inactivity-logout'));
            }
        }, 60 * 1000);
    }

    setupRefreshTimer() {
        // Clear existing timer to prevent leaks
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
        }

        this.refreshTimer = setInterval(() => {
            if (!this.isAuthenticated()) return; // Skip if no active session

            this.refreshAccessToken().catch(() => {
                // refreshAccessToken already logs out and toasts on failure
            });
        }, this.REFRESH_INTERVAL);
    }

    async validateUser() {
        const now = Date.now();
        if (this.roleCache.result !== null &&
            this.roleCache.timestamp &&
            (now - this.roleCache.timestamp) < this.roleCache.CACHE_DURATION) {
            return this.roleCache.result;
        }

        try {
            const response = await this.authenticatedFetch(`${BASE_URL}/users/validate`, { silent: true });
            if (!response.ok) {
                this.roleCache.result = null;
                this.roleCache.timestamp = now;
                return null;
            }
            const data = await response.json();
            const user = data.result?.user || null;
            this.roleCache.result = user;
            this.roleCache.timestamp = now;
            return user;
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Role validation error:', error);
            }
            return null;
        }
    }

    async validateUserRole() {
        const user = await this.validateUser();
        return user?.role || null;
    }

    // Sends the httpOnly auth cookies automatically via `credentials: 'include'` -
    // no Authorization header is attached, since the token is never readable by JS.
    // `silent` is passed through to refreshAccessToken() - see its comment.
    async authenticatedFetch(url, { silent = false, ...fetchOptions } = {}) {
        let response = await fetch(url, { ...fetchOptions, credentials: 'include' });

        if (response.status === 401) {
            // refreshAccessToken() already logs out and (unless silent) toasts on
            // failure - let its rejection propagate rather than duplicating that here.
            await this.refreshAccessToken({ silent });
            response = await fetch(url, { ...fetchOptions, credentials: 'include' });
        }

        return response;
    }


    logout() {
        // Clear timers to prevent memory leaks
        if (this.inactivityTimer) {
            clearInterval(this.inactivityTimer);
            this.inactivityTimer = null;
        }
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
            this.refreshTimer = null;
        }
        this.isInitialized = false;

        // Clear all storage for complete cleanup
        localStorage.removeItem(SESSION_FLAG);
        localStorage.removeItem('userId');
        localStorage.removeItem('name');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('role');

        // Legacy cleanup from pre-httpOnly-cookie versions of this app
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('mfaRequired');
        localStorage.removeItem('isSuperAdmin');
        sessionStorage.removeItem('accessToken');

        // Clear role cache so next login gets a fresh role check
        this.roleCache.result = null;
        this.roleCache.timestamp = null;

        // Best-effort: ask the backend to clear the httpOnly cookies too. Fire
        // and forget - local state is already cleared regardless of the outcome.
        axios.post(`${BASE_URL}/users/logout`, {}, { withCredentials: true }).catch((err) => {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Best-effort server-side logout call failed:', err);
            }
        });

        window.dispatchEvent(new CustomEvent('auth:logout'));
    }
}

export const authManager = new AuthManager();
